import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getKeyRiskCriteria,
  getTprmCriteria,
  updateKeyRiskCriteria,
  updateTprmCriteria,
} from '../reviewSettingsApi';
import { normalizeTprmCriteria } from './editorBits';

// Owns all data + draft state for the playbook editors. Drafts live here (not
// in the editor components) so switching tabs never drops unsaved edits, and
// the page stays presentational.
export function usePlaybookEditors({ showToast }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // TPRM criteria (structured object).
  const [tprm, setTprm] = useState(null);
  const [tprmDraft, setTprmDraft] = useState(null);
  const [tprmVersion, setTprmVersion] = useState('');
  const [tprmName, setTprmName] = useState('');
  const [tprmSaving, setTprmSaving] = useState(false);

  // Key-term criteria (structured object: default + byType + byCategory).
  const [keyRisk, setKeyRisk] = useState(null);
  const [keyRiskDraft, setKeyRiskDraft] = useState(null);
  const [keyRiskVersion, setKeyRiskVersion] = useState('');
  const [keyRiskName, setKeyRiskName] = useState('');
  const [keyRiskSaving, setKeyRiskSaving] = useState(false);

  const applyTprm = useCallback((res) => {
    setTprm(res);
    setTprmDraft(res.criteria || {});
    setTprmVersion(res.version || '');
    setTprmName(res.name || '');
  }, []);

  const applyKeyRisk = useCallback((res) => {
    setKeyRisk(res);
    setKeyRiskDraft(res.criteria || {});
    setKeyRiskVersion(res.version || '');
    setKeyRiskName(res.name || '');
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getTprmCriteria(), getKeyRiskCriteria()])
      .then(([tprmRes, keyRiskRes]) => {
        if (cancelled) return;
        applyTprm(tprmRes);
        applyKeyRisk(keyRiskRes);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [applyTprm, applyKeyRisk]);

  const tprmDirty = !!tprm && (
    JSON.stringify(tprmDraft) !== JSON.stringify(tprm.criteria || {}) ||
    tprmVersion !== (tprm.version || '') ||
    tprmName !== (tprm.name || '')
  );

  const keyRiskDirty = !!keyRisk && (
    JSON.stringify(keyRiskDraft) !== JSON.stringify(keyRisk.criteria || {}) ||
    keyRiskVersion !== (keyRisk.version || '') ||
    keyRiskName !== (keyRisk.name || '')
  );

  const saveTprm = useCallback(async () => {
    setTprmSaving(true);
    try {
      const criteria = normalizeTprmCriteria(tprmDraft);
      applyTprm(await updateTprmCriteria({ criteria, name: tprmName, version: tprmVersion }));
      showToast('TPRM criteria saved', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setTprmSaving(false);
    }
  }, [tprmDraft, tprmName, tprmVersion, applyTprm, showToast]);

  // Edit one top-level section of the TPRM criteria, preserving the rest
  // (including any keys the form doesn't surface).
  const setTprmField = useCallback((key, val) => {
    setTprmDraft((prev) => ({ ...(prev || {}), [key]: val }));
  }, []);

  const saveKeyRisk = useCallback(async () => {
    setKeyRiskSaving(true);
    try {
      applyKeyRisk(await updateKeyRiskCriteria({ criteria: keyRiskDraft, name: keyRiskName, version: keyRiskVersion }));
      showToast('Key-term criteria saved', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setKeyRiskSaving(false);
    }
  }, [keyRiskDraft, keyRiskName, keyRiskVersion, applyKeyRisk, showToast]);

  const setKeyRiskField = useCallback((key, val) => {
    setKeyRiskDraft((prev) => ({ ...(prev || {}), [key]: val }));
  }, []);

  const tprmEditor = useMemo(() => ({
    base: tprm,
    draft: tprmDraft, setField: setTprmField, replaceDraft: setTprmDraft,
    version: tprmVersion, setVersion: setTprmVersion,
    name: tprmName, setName: setTprmName,
    dirty: tprmDirty, saving: tprmSaving,
    save: saveTprm,
    reset: () => applyTprm(tprm),
  }), [tprm, tprmDraft, setTprmField, setTprmDraft, tprmVersion, tprmName, tprmDirty, tprmSaving, saveTprm, applyTprm]);

  const keyRiskEditor = useMemo(() => ({
    base: keyRisk,
    draft: keyRiskDraft, setField: setKeyRiskField, replaceDraft: setKeyRiskDraft,
    version: keyRiskVersion, setVersion: setKeyRiskVersion,
    name: keyRiskName, setName: setKeyRiskName,
    dirty: keyRiskDirty, saving: keyRiskSaving,
    save: saveKeyRisk,
    reset: () => applyKeyRisk(keyRisk),
  }), [keyRisk, keyRiskDraft, setKeyRiskField, keyRiskVersion, keyRiskName, keyRiskDirty, keyRiskSaving, saveKeyRisk, applyKeyRisk]);

  return { loading, error, tprm: tprmEditor, keyRisk: keyRiskEditor };
}
